import type { Draft } from '../../../lib/types';
import { t } from '../../../lib/i18n';

/**
 * What changes for visitors (Doc 04 §5).
 *
 * Separate from the component that renders it, because this function is the publish gate:
 * an empty result makes the toolbar report "live" and hide the button. A gate deserves a
 * test, and a test should not have to pull in React to get one.
 */
export interface Change {
  path: string;
  before: string;
  after: string;
  /** True when this change alters what the user was told, and so bumps policy_version. */
  material: boolean;
}

/**
 * Metadata: it differs between a draft and the version it came from by definition, and
 * none of it is a change a visitor could notice.
 */
const META = new Set([
  'id', 'site_id', 'status', 'dirty', 'based_on_version', 'policy_version',
  'config_version', 'published_at', 'created_at', 'updated_at',
]);

/** Every leaf, as a dotted path. Arrays are compared whole — order is meaningful here. */
function leaves(value: unknown, prefix: string, out: Map<string, string>): void {
  if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      leaves(v, prefix ? `${prefix}.${k}` : k, out);
    }
    return;
  }
  out.set(prefix, JSON.stringify(value ?? null));
}

/**
 * Paths whose change alters what the visitor was told, and therefore the proof hash.
 *
 * Anything not matched here is presentation. Getting this wrong in the safe direction
 * costs a needless re-prompt; the other direction silently keeps a consent that was given
 * for different wording.
 */
const MATERIAL = [
  /^texts\.[^.]+\.(title|body)$/,
  /^ui\.buttons\.[^.]+\.(label|show|hide_prefs)$/,
  /^categories\.\d+\.(default|required|slug)$/,
  /^categories\.\d+\.labels\./,
  /^ui\.prefs\.(title|intro)$/,
  /^ui\.links\./,
];

/**
 * The last segment of a path, under the name the editor gives that field.
 *
 * German values rather than `t('…')` calls, because the lookup is what picks the string —
 * see `lib/i18n.tsx`. Anything missing here falls through as its raw key, which is ugly
 * and honest.
 */
const FIELD: Record<string, string> = {
  action: 'Aktion',
  align: 'Ausrichtung',
  alt: 'Alternativtext',
  animation: 'Einblendung',
  block_page: 'Seite sperren',
  body: 'Fließtext',
  button_full_width: 'Buttons über die volle Breite',
  button_layout: 'Button-Anordnung',
  esc: 'Escape-Taste',
  family: 'Schriftfamilie',
  font: 'Schriftart',
  fontSize: 'Schriftgröße',
  gap: 'Abstand',
  headline: 'Überschrift',
  height: 'Höhe',
  hide_prefs: 'im Auswahl-Dialog ausgeblendet',
  intro: 'Einleitung',
  label: 'Text',
  layout: 'Darstellung',
  mode: 'Modus',
  order: 'Reihenfolge',
  overlay: 'Overlay',
  overlay_click: 'Klick auf den Hintergrund',
  padding: 'Innenabstand',
  position: 'Position',
  prefs_order: 'Reihenfolge im Auswahl-Dialog',
  radius: 'Ecken-Radius',
  shadow: 'Schatten',
  show: 'sichtbar',
  show_cookies: 'Cookie-Namen',
  show_vendors: 'Anbieterliste',
  style: 'Stil',
  title: 'Titel',
  url: 'Adresse',
  width: 'Breite',
  width_mobile: 'Breite auf Mobilgeräten',
};

/** The panel a `ui.<group>.…` path belongs to, so a row names where to go and fix it. */
const GROUP: Record<string, string> = {
  behaviour: 'Verhalten',
  box: 'Größe & Abstände',
  buttons: 'Buttons',
  close: 'Schließen-Kreuz',
  fonts: 'Schrift',
  logo: 'Logo',
  prefs: 'Auswahl-Dialog',
  reopen: 'Badge',
};

/** The per-language copy, which is filed by field name rather than by panel. */
const TEXT: Record<string, string> = {
  title: 'Überschrift ({lang})',
  body: 'Text ({lang})',
  links: 'Rechtliche Links ({lang})',
  prefs_title: 'Titel des Auswahl-Dialogs ({lang})',
  prefs_intro: 'Einleitung des Auswahl-Dialogs ({lang})',
  always_active: 'Beschriftung „immer aktiv“ ({lang})',
  close_label: 'Beschriftung des Schließen-Kreuzes ({lang})',
};

/** One or more path segments, translated where they have a name. */
const fields = (rest: string): string =>
  rest.split('.').map((k) => (FIELD[k] ? t(FIELD[k]!) : k)).join(' · ');

/**
 * Human-readable names for the paths people actually recognise.
 *
 * A path that reaches the fallback prints as itself — `gcm.ads_data_redaction` — which is
 * ugly and honest. What it must never print as is nothing: an unnamed row is still a row,
 * and the alternative to an ugly name is a change that publishes unannounced.
 */
function label(path: string): string {
  const txt = /^texts\.([^.]+)\.(.+)$/.exec(path);
  if (txt) {
    const name = TEXT[txt[2]!];
    return name ? t(name, { lang: txt[1]! }) : `${fields(txt[2]!)} (${txt[1]})`;
  }
  if (path === 'ui.layout') return t('Layout');
  if (path === 'ui.buttons.equal_weight') return t('Gleichgewichtung');
  if (path === 'ui.reopen') return t('Badge');
  const theme = /^ui\.theme\.(.+)$/.exec(path);
  if (theme) return t('Theme · {key}', { key: fields(theme[1]!) });
  const btn = /^ui\.buttons\.([^.]+)\.(label|show|style|hide_prefs)$/.exec(path);
  if (btn) return `${t('Button')} · ${btn[1]} · ${fields(btn[2]!)}`;
  const cat = /^categories\.(\d+)\.(.+)$/.exec(path);
  if (cat) return t('Kategorie {slug} · {field}', { slug: cat[1]!, field: cat[2]! });
  if (path === 'categories') return t('Kategorien');
  if (path === 'i18n_available') return t('Sprachen');
  const gcm = /^gcm\.(.+)$/.exec(path);
  if (gcm) return t('Google Consent Mode · {key}', { key: gcm[1]! });
  const group = /^ui\.([^.]+)\.(.+)$/.exec(path);
  if (group && GROUP[group[1]!]) return `${t(GROUP[group[1]!]!)} · ${fields(group[2]!)}`;
  return fields(path.replace(/^ui\./, ''));
}

/**
 * One value, written out the way the editor talks about it.
 *
 * `JSON.stringify` used to do this job, which was fine for a number and unreadable for
 * everything else: a pair of legal links arrived as `[{"label":"Datenschutz","url":…`,
 * cut off by the column before the second one, and a reader could not tell what had
 * changed or even that anything had.
 *
 * Only arrays and primitives ever get here — `leaves` walks into objects — but the object
 * branch stays for the two shapes that live inside an array.
 */
function one(v: unknown): string {
  if (v === undefined || v === null || v === '') return '—';
  if (Array.isArray(v)) return v.length === 0 ? '—' : v.map(one).join(', ');
  if (typeof v === 'boolean') return v ? t('ja') : t('nein');
  if (typeof v !== 'object') return String(v);

  const o = v as Record<string, unknown>;
  // A legal link, as the editor stores it.
  if ('url' in o && 'label' in o) return `${o['label'] || '—'} (${o['url'] || '—'})`;
  // A category. Its name and its two switches are what a reader can act on; the per-
  // language description is not summarised here, so an edit to one shows as a row whose
  // sides read alike. Naming that would mean a row per language, which is the array walk
  // this diff deliberately does not do — see `leaves`.
  if (typeof o['slug'] === 'string') {
    const named = Object.values((o['labels'] ?? {}) as Record<string, { label?: string }>)
      .map((l) => l?.label).find(Boolean);
    const flags = [o['required'] ? t('Pflicht') : '', o['default'] ? t('vorausgewählt') : '']
      .filter(Boolean);
    return `${named ? `${named} (${o['slug']})` : String(o['slug'])}`
      + (flags.length > 0 ? ` [${flags.join(', ')}]` : '');
  }
  return Object.entries(o).map(([k, x]) => `${fields(k)}: ${one(x)}`).join(', ');
}

/**
 * Long values are cut between items, never inside one.
 *
 * The limit is generous because the row wraps now — it is there for the pathological
 * value (a font stack, twenty links), not for the ordinary long one, and the list has to
 * stay scannable at a glance. Where it does cut, it cuts at the last separator that
 * fits, so what is left is whole entries and an ellipsis rather than half a URL.
 */
function clip(s: string, max = 160): string {
  if (s.length <= max) return s;
  const at = s.lastIndexOf(', ', max);
  return `${s.slice(0, at > max / 3 ? at : max).trimEnd()} …`;
}

const show = (v: unknown): string => clip(one(v));

/**
 * Every difference between the published version and the draft.
 *
 * This walks both objects rather than comparing a hand-written list of fields, and that
 * is not tidiness: the list decided whether the publish button appeared at all. It named
 * texts, layout, eight theme keys, four buttons, the badge and the categories — and not
 * the logo, the spacing, the overlay behaviour, the preferences dialog, the legal links
 * or (once they existed) the fonts. A change to any of those produced an empty diff, so
 * the toolbar reported "live", offered no button, and the change could never be
 * published. Whether something is publishable has to be exhaustive; only the naming of it
 * is allowed to be curated.
 */
export function diffDrafts(saved: Draft, draft: Draft): Change[] {
  const a = new Map<string, string>();
  const b = new Map<string, string>();
  leaves(saved, '', a);
  leaves(draft, '', b);

  const out: Change[] = [];
  for (const path of new Set([...a.keys(), ...b.keys()])) {
    if (META.has(path.split('.')[0]!)) continue;
    const before = a.get(path);
    const after = b.get(path);
    if (before === after) continue;
    out.push({
      path: label(path),
      before: show(before === undefined ? undefined : JSON.parse(before)),
      after: show(after === undefined ? undefined : JSON.parse(after)),
      material: MATERIAL.some((re) => re.test(path)),
    });
  }
  return out.sort((x, y) => x.path.localeCompare(y.path));
}

/**
 * What the publish button offers, given that diff.
 *
 * `unchanged` is deliberately not `live`, and the distinction is the whole point: rules,
 * vendors and their exceptions are not part of the draft. The server reads them at
 * publish time (`api/sites.go`, buildBundle), so an empty diff means "the banner is
 * identical", never "visitors have everything you configured". Rendering that as a dead
 * "✓ Live" badge left the console with no publish control at all — a blocking exception
 * added on the blocking screen could be saved, was shown as effective, and could not be
 * shipped from anywhere in the product.
 *
 * So an empty diff still offers the button. It just offers it quietly.
 */
export type PublishState = 'blocked' | 'first' | 'unchanged' | 'ready';

export function publishState(
  publishable: boolean,
  firstPublish: boolean,
  pendingChanges: number,
): PublishState {
  if (!publishable) return 'blocked';
  if (firstPublish) return 'first';
  return pendingChanges === 0 ? 'unchanged' : 'ready';
}
